import { createFileRoute } from "@tanstack/react-router";
import {
  LiveRatesTable,
  LiveChart,
  DeviationBadge,
  KaratWeightCalc,
  KsaLinkMatrix,
  FaqAccordion,
  buildKsaSchema,
  arDateParts,
  goldRatesQueryOptions,
} from "@/components/sa-ar/shell";
import { computeSaPrices, formatSar, SA_KARATS, GRAMS_PER_OZ } from "@/lib/sa-gold/data";
import { useGoldRates } from "@/lib/nonblocking-rates";

const SLUG = "/sa/ar/gold-prices-today";
const CANONICAL = `https://drhint.com${SLUG}`;

const CITIES_AR = ["الرياض", "جدة", "مكة المكرمة", "المدينة المنورة", "الدمام", "الخبر", "تبوك", "أبها", "الطائف", "بريدة"];

const FAQS = [
  { q: "كم سعر جرام الذهب عيار 21 اليوم في السعودية؟", a: "سعر جرام الذهب عيار 21 اليوم في السعودية يتم تحديثه لحظياً في الجدول أعلى الصفحة، وهو مشتق مباشرة من سعر السبائك العالمي (XAU) مضروباً في سعر الربط الثابت 3.75 ريال للدولار مقسوماً على 31.1035 جراماً، مع إضافة هامش الصاغة." },
  { q: "هل تتغير أسعار الذهب في الرياض وجدة عن باقي المدن السعودية؟", a: "السعر الأساسي موحد لأنه مرتبط بسعر البورصة العالمية. الفروقات لا تتجاوز عادة 0.3–0.8 ريال للجرام وتعود إلى تكاليف المحلات وأجرة الصياغة في وسط الرياض وجدة." },
  { q: "هل يشمل سعر الذهب المعلن اليوم في السعودية قيمة المصنعية؟", a: "لا. الأسعار المعروضة هنا هي أسعار المعدن الخام فقط. أجرة الصياغة تُضاف بشكل منفصل في محلات الصاغة، وتتراوح عادة بين 5 و 40 ريال للجرام حسب دقة التصميم." },
  { q: "متى تنخفض أسعار الذهب في السوق السعودي لشراء المشغولات؟", a: "الأسعار تنخفض عادة عند ارتفاع الدولار عالمياً، وفي أيام هدوء الطلب المحلي (منتصف الأسبوع وبعيداً عن مواسم الأعراس ورمضان)." },
];

export const Route = createFileRoute("/sa/ar/gold-prices-today")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const title = `اسعار الذهب اليوم في السعودية — تحديث مباشر`;
    const description = `تابع أسعار الذهب اليوم في السعودية تحديث يومي مباشر لعيار 21، 24، 22، 18 وأسعار سبائك الذهب والذهب المستعمل بالريال السعودي والدولار في الرياض وجدة والدمام.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "ar-SA" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "ar_SA" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "ar-SA", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildKsaSchema({ slug: SLUG, name: title, description, faqs: FAQS }),
          ),
        },
      ],
    };
  },
  component: DailyPricePage,
});

function DailyPricePage() {
  const dp = arDateParts();
  return (
    <main dir="rtl" className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">KSA · مباشر</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          اسعار الذهب اليوم في السعودية — تحديث مباشر {dp.day} {dp.dayNum} {dp.month} {dp.year}
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          تحديث لحظي لأسعار الذهب في السوق السعودي من سعر السبائك العالمية، معدّلاً على الريال السعودي ومحدّثاً كل 15 دقيقة. الجدول أدناه يوضح جرام، 10 جرام، أونصة وكيلو لجميع الأعيار.
        </p>
        <div className="mt-3"><DeviationBadge /></div>
      </header>
      <LiveChart title={`الرسم البياني اللحظي — الذهب في السعودية ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <MultiKaratGrid />
      <CitiesFanout />
      <KaratWeightCalc />
      <FaqAccordion items={FAQS} />
      <KsaLinkMatrix current={SLUG} />
    </main>
  );
}

function MultiKaratGrid() {
  const { data } = useGoldRates();
  const p = computeSaPrices({ spotUsdPerOz: data.spotUsdPerOz });
  const karats = [24, 22, 21, 18, 14, 12] as const;
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <h2 className="text-xl md:text-2xl font-bold mb-3">تحديث مباشر لأسعار الذهب في السعودية اليوم</h2>
      <p className="text-sm text-muted-foreground mb-4">تفصيل السعر لكل عيار — جرام، أونصة، كيلو — بالريال السعودي، مبني على السعر العالمي اللحظي.</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {karats.map((k) => {
          const base = k <= 14 ? p[14] * (k / 14) : p[k as (typeof SA_KARATS)[number]] ?? p[24] * (k / 24);
          return (
            <div key={k} className="rounded-2xl border border-amber-500/25 bg-card p-4">
              <div className="text-sm font-bold text-amber-600 dark:text-amber-300">عيار {k}</div>
              <div className="mt-2 text-2xl font-extrabold tabular-nums">{formatSar(base)} <span className="text-xs font-normal text-muted-foreground">/ جرام</span></div>
              <div className="mt-1 text-xs text-muted-foreground tabular-nums">أونصة: {formatSar(base * GRAMS_PER_OZ)} · كيلو: {formatSar(base * 1000)}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CitiesFanout() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-4">
      <h2 className="text-lg md:text-xl font-bold mb-2">أسعار الذهب في المدن السعودية اليوم</h2>
      <p className="text-sm text-muted-foreground">
        السعر الأساسي موحد على مستوى المملكة، والفروقات الطفيفة بين المدن تعكس تكلفة النقل وهامش الصاغة المحلي فقط.
      </p>
      <details className="mt-3 rounded-xl border border-border bg-muted/30 p-4">
        <summary className="cursor-pointer font-semibold">عرض قائمة المدن</summary>
        <ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
          {CITIES_AR.map((c) => (
            <li key={c} className="rounded-lg bg-card border border-border px-3 py-2">
              أسعار الذهب اليوم في {c}
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}